import React, { useRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { Link } from '@driftt/dds'

import ActivityContentToolTip from 'components/ActivityContentTooltip/index'
import { AccountActivityAccountAvatar } from 'components/Avatars'
import usePathUtils from 'hooks/usePathUtils'
import { AccountActivity } from 'modules/accountActivity/domain'
import { Account, ActivityEndUser, CompanyData } from 'modules/accountActivity/domain'
import { EngagementScore } from 'pages/AccountsPage'
import { pluralize } from 'utils/formatStrings'
import { accountPath } from 'utils/paths'
import { timeSinceUpdated } from 'utils/timeStamp'
import { segmentTrack, TrackType } from 'utils/tracking'

import styles from './styles.module.css'

const StyledLink = ({ children, ...rest }: any) => (
  <Link {...rest} as="a" variant="object">
    {children}
  </Link>
)

interface CardHeaderProps {
  insightId: number
  visitorList?: ActivityEndUser[]
  accountData?: Account
  accountActivity?: AccountActivity
  companyData?: CompanyData
  timestampMs: number
  title?: React.ReactElement
}

const CardHeader: React.FC<CardHeaderProps> = ({
  insightId,
  visitorList = [],
  accountData,
  companyData,
  accountActivity,
  timestampMs,
  title
}) => {
  const { isAccountDetailsPage } = usePathUtils()

  const accountDisplayName =
    accountData?.name || companyData?.name || companyData?.domain || 'Unnamed account'

  const ref = useRef<HTMLDivElement>(null)
  const refElement = useRef<HTMLDivElement>(null)

  const createCompanyLinkWithName = (data: Account, companyData?: CompanyData) => (
    <>
      {companyData ? (
        <ActivityContentToolTip data={companyData}>
          <RouterLink
            component={StyledLink}
            to={accountPath(data.accountId)}
            onClick={() => {
              segmentTrack(TrackType.INSIGHT_FEED_VIEW_ACCOUNT, {
                insightId: insightId,
                accountId: data.accountId
              })
            }}>
            <div ref={refElement}>{accountDisplayName}</div>
          </RouterLink>
        </ActivityContentToolTip>
      ) : (
        <RouterLink
          component={StyledLink}
          to={accountPath(data.accountId)}
          onClick={() => {
            segmentTrack(TrackType.INSIGHT_FEED_VIEW_ACCOUNT, {
              insightId: insightId,
              accountId: data.accountId
            })
          }}>
          <div ref={refElement}>{accountDisplayName}</div>
        </RouterLink>
      )}
    </>
  )

  return (
    <div className={styles.headerContainer} ref={ref} id="headerContainer">
      <div className={styles.avatarAndContact}>
        {accountData ? (
          <RouterLink
            to={accountPath(accountData.accountId)}
            onClick={() => {
              segmentTrack(TrackType.INSIGHT_FEED_VIEW_ACCOUNT, {
                insightId: insightId,
                accountId: accountData?.accountId
              })
            }}>
            <AccountActivityAccountAvatar imageUrl={companyData?.logo} />
          </RouterLink>
        ) : (
          <AccountActivityAccountAvatar imageUrl={companyData?.logo} />
        )}
        <div className={styles.headerText}>
          {title ?? (
            <div className={styles.firstContactAndCompany}>
              {accountData && createCompanyLinkWithName(accountData, companyData)}
              {visitorList.length > 0 && (
                <div className={styles.users}>
                  {' '}
                  {`${pluralize('user', visitorList.length)}`}{' '}
                  {visitorList.length > 1 ? 'were' : 'was'} active on your site.
                </div>
              )}
            </div>
          )}
          <div className={styles.cardSubheader}>
            <span className={styles.cardSubheaderText}>{`${timeSinceUpdated(
              timestampMs
            )} ago`}</span>
          </div>
        </div>
      </div>
      {accountActivity && !isAccountDetailsPage && (
        <div className={styles.engagementScore}>
          <EngagementScore boundaryRef={ref.current} accountActivity={accountActivity} />
        </div>
      )}
    </div>
  )
}

export default CardHeader
